import React from "react";

/**
 * Last line of defence for a render exception.
 *
 * Without this mounted, any throw during render unmounts the whole tree and
 * leaves a white screen — no message, no way out — which on this app means
 * mid-estimate, in a driveway, on a phone. The fallback's job is therefore
 * not to explain the stack; it is to (a) say the work is not lost and
 * (b) give one obvious button that gets back to a working app.
 *
 * Reload is the right recovery here because both shells debounce-save the
 * estimate to the cloud draft on every edit, so a reload re-bootstraps from
 * that draft. The error text stays available behind a disclosure so a
 * screenshot is still useful for diagnosis.
 */
export default class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error: any }
> {
  constructor(props: any) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: any) {
    return { error };
  }

  componentDidCatch(error: any, info: any) {
    // eslint-disable-next-line no-console
    console.error("App crashed:", error, info);
    // Desktop has no devtools in the packaged build — route it to startup.log
    // so a crash in the field is still diagnosable after the fact.
    try {
      (window as any).estimator?.logLine?.(
        `RENDER CRASH: ${error?.message || String(error)} | ${String(
          info?.componentStack || ""
        ).slice(0, 400)}`
      );
    } catch {
      /* diagnostics must never throw from inside the error boundary */
    }
  }

  render() {
    if (!this.state.error) return this.props.children;

    const detail =
      this.state.error?.stack ||
      this.state.error?.message ||
      String(this.state.error);

    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 20,
          background: "var(--bg, #0a0a0a)",
          color: "var(--ink, #fff)",
          fontFamily:
            "system-ui, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif",
        }}
      >
        <div
          style={{
            width: "min(520px, 100%)",
            background: "var(--surface, #141414)",
            border: "1px solid var(--border, #1f1f1f)",
            borderRadius: 14,
            padding: 22,
          }}
        >
          <h2 style={{ margin: 0, fontSize: 19, fontWeight: 700 }}>
            Something went wrong
          </h2>
          <p
            style={{
              marginTop: 10,
              marginBottom: 0,
              fontSize: 14,
              lineHeight: 1.5,
              color: "var(--ink-2, #c7c7c7)",
            }}
          >
            Your estimate is auto-saved — reloading brings back the most recent
            saved draft.
          </p>

          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              marginTop: 18,
              width: "100%",
              minHeight: 48,
              fontSize: 15,
              fontWeight: 700,
              color: "#fff",
              background: "var(--accent, #3e6ae1)",
              border: 0,
              borderRadius: 12,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            Reload the app
          </button>

          <details style={{ marginTop: 16 }}>
            <summary
              style={{
                cursor: "pointer",
                fontSize: 12,
                color: "var(--ink-3, #8a8a8a)",
              }}
            >
              Error details (screenshot this if it keeps happening)
            </summary>
            <pre
              style={{
                marginTop: 8,
                marginBottom: 0,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                maxHeight: 220,
                overflow: "auto",
                background: "var(--surface-2, #1c1c1c)",
                color: "var(--ink-2, #c7c7c7)",
                padding: 12,
                borderRadius: 8,
                fontSize: 11,
                lineHeight: 1.45,
              }}
            >
              {detail}
            </pre>
          </details>
        </div>
      </div>
    );
  }
}
